import { FC, useEffect, useState } from 'react'
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil'
import { Button } from 'src/components/button'
import * as atoms from 'src/atoms/user-lists/user-lists-modal'
import { Typography } from '@mui/material'
import styled from '@emotion/styled'
import { Colors } from 'src/components/theme'
import { userListsClient } from 'src/clients'
import { browserLogger } from 'src/utils/browser-logger'
import { Spinner } from 'src/components/spinner'
import { Alert } from 'src/components/alert/alert'

const ROW_LIMIT = 1000

function convertCSVFileToStringArray(file: File): Promise<string[]> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = async (e) => {
      const text = e.target?.result as string
      const list = text.trim().split('\n')
      const listWithoutQuotes = list.map((a) => a.replace(/"/g, ''))
      const listWithoutHeader = listWithoutQuotes.slice(1)
      resolve(listWithoutHeader)
    }
    reader.onerror = () => {
      reject('Error reading file')
    }
    reader.readAsText(file)
  })
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const PreviewBox = styled.div`
  border: 1px dashed ${Colors.neutral100};
  padding: 16px;
  max-height: 300px;
  max-width: 1120px;
  overflow-y: auto;
`

const Footer = styled.div`
  display: flex;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
`

const Actions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
`

const MDButton = styled(Button)`
  background-color: ${Colors.blueDark};
`

const CAButton = styled(Button)`
  border: 1px solid ${Colors.blueDark};
  color: ${Colors.blueDark};
`

const AddressText = styled(Typography)`
  padding: 6px 0;
`

export const MatchDataContent: FC = () => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>()
  const setUploadSection = useSetRecoilState(atoms.uploadSection)
  const setMatchedData = useSetRecoilState(atoms.matchedData)
  const file = useRecoilValue(atoms.csvFile)
  const resetCsvFile = useResetRecoilState(atoms.csvFile)
  const [addresses, setAddresses] = useState<string[]>([])

  const getAddresses = async () => {
    if (!file) {
      return
    }
    const data = await convertCSVFileToStringArray(file)
    setAddresses(data)
  }

  useEffect(() => {
    void getAddresses()
  }, [file])

  const matchData = async () => {
    if (!file || loading || !addresses.length) {
      return
    }
    setLoading(true)
    setError(undefined)
    try {
      const res = await userListsClient.matchItems(addresses)
      setMatchedData(
        addresses.map((address, i) => {
          return {
            address,
            googlePlace: res[i],
          }
        })
      )
      setUploadSection('match-data-results')
    } catch (error) {
      browserLogger.error('Error matching data', error)
      setError('Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const surpassedLimit = addresses.length > ROW_LIMIT

  const onClearAll = () => {
    resetCsvFile()
    setUploadSection('upload-csv')
  }

  return (
    <Container>
      <Typography>Preview of CSV</Typography>
      <PreviewBox>
        {addresses.map((address, index) => (
          <AddressText key={index} textAlign="center" noWrap>
            {address}
          </AddressText>
        ))}
      </PreviewBox>
      <Footer>
        <div>
          {!error && surpassedLimit && <Alert color="warning" message={`Can't import more than ${ROW_LIMIT} rows`} />}
        </div>
        <div>{!!error && <Alert color="error" message={error} />}</div>
        <Actions>
          <CAButton onClick={onClearAll} variant="outlined">
            Clear All
          </CAButton>
          <MDButton
            onClick={matchData}
            variant="contained"
            startIcon={loading && <Spinner size={14} />}
            disabled={surpassedLimit}>
            Match Data
          </MDButton>
        </Actions>
      </Footer>
    </Container>
  )
}
